import { useEffect, useRef, useState } from 'react';
import { useMonitorDeliveries } from './api/get-monitor-deliveries';
import { useMonitorDeliveryDetail } from './api/get-monitor-delivery-detail';
import { useMonitorIndicators } from './api/get-monitor-indicators';
import { BackToTopButton } from './components/back-to-top/back-to-top';
import { DeliveriesGrid } from './components/deliveries-grid/deliveries-grid';
import { DeliveryDetailDrawer } from './components/delivery-detail-drawer/delivery-detail-drawer';
import { CardsSkeleton } from './components/empty-state/cards-skeleton';
import { MonitorEmptyState } from './components/empty-state/empty-state';
import { MonitorFilters } from './components/filters/filters';
import { MonitorIndicators } from './components/indicators/indicators';
import { LoadMoreTrigger } from './components/load-more-trigger/load-more-trigger';
import { RefreshCountdown } from './components/refresh-countdown/refresh-countdown';
import { MonitorStatusTabs } from './components/status-tabs/status-tabs';
import {
  MONITOR_LOAD_MORE_SKELETON_CARDS,
  MONITOR_PAGE_SIZE,
} from './constants';
import { useMinutesSince } from './hooks/use-minutes-since';
import { useMonitorFilters } from './hooks/use-monitor-filters';
import { projectMonitorDelivery } from './utils';
import styles from './monitor-deliveries.module.scss';

function MonitorDeliveriesMain() {
  const {
    cityId,
    tab,
    code,
    deliveryType,
    deliveryId,
    setCityId,
    setTab,
    setCode,
    setDeliveryType,
    setDeliveryId,
    citiesOptions,
    loadingCities,
  } = useMonitorFilters();

  const [limit, setLimit] = useState(MONITOR_PAGE_SIZE);
  const filtersKey = [cityId, tab, code, deliveryType].join('|');
  const [limitFiltersKey, setLimitFiltersKey] = useState(filtersKey);
  if (filtersKey !== limitFiltersKey) {
    setLimitFiltersKey(filtersKey);
    setLimit(MONITOR_PAGE_SIZE);
  }

  const {
    data,
    dataUpdatedAt,
    errorUpdatedAt,
    isFetching,
    isLoading,
    isError,
    isPreviousData,
    refetch,
  } = useMonitorDeliveries(
    { cityId, tab, code, deliveryType, limit },
    { enabled: !loadingCities },
  );

  const { data: indicators, isLoading: isLoadingIndicators } =
    useMonitorIndicators({ cityId }, { enabled: !loadingCities });

  const [drawerDeliveryId, setDrawerDeliveryId] = useState(deliveryId);
  if (deliveryId && deliveryId !== drawerDeliveryId) {
    setDrawerDeliveryId(deliveryId);
  }

  const deepLinkDeliveryId = useRef(deliveryId);
  const { data: deepLinkDetail } = useMonitorDeliveryDetail(
    deepLinkDeliveryId.current === deliveryId ? deliveryId : undefined,
  );

  useEffect(() => {
    if (!deepLinkDetail || loadingCities) {
      return;
    }

    deepLinkDeliveryId.current = undefined;

    if (deepLinkDetail.city.id !== cityId) {
      setCityId(deepLinkDetail.city.id);
    }
  }, [deepLinkDetail, loadingCities]);

  const closeDetail = () => {
    setDeliveryId(undefined);
    document
      .querySelector<HTMLElement>(`[data-monitor-delivery-id="${deliveryId}"]`)
      ?.focus();
  };

  const lastUpdatedAt = Math.max(dataUpdatedAt, errorUpdatedAt);

  const minutesSinceUpdate = useMinutesSince(dataUpdatedAt);
  const deliveries = data?.deliveries.map(delivery =>
    projectMonitorDelivery(delivery, minutesSinceUpdate),
  );

  const isLoadingMore = isPreviousData && limit > MONITOR_PAGE_SIZE;
  const canLoadMore =
    !!data &&
    !isPreviousData &&
    data.deliveries.length >= limit &&
    data.deliveries.length < data.counts[tab];

  const loadMore = () => setLimit(current => current + MONITOR_PAGE_SIZE);

  function renderDeliveries() {
    if (loadingCities || isLoading) {
      return <CardsSkeleton />;
    }

    if (isError && !deliveries) {
      return <MonitorEmptyState variant="error" onRetry={() => refetch()} />;
    }

    if (!deliveries) {
      return null;
    }

    if (deliveries.length === 0) {
      return code ? (
        <MonitorEmptyState
          variant="noSearchResult"
          code={code}
          onClearSearch={() => setCode('')}
        />
      ) : (
        <MonitorEmptyState variant="empty" />
      );
    }

    return (
      <>
        <DeliveriesGrid
          deliveries={deliveries}
          selectedDeliveryId={deliveryId}
          onOpenDetail={setDeliveryId}
        />
        {isLoadingMore && (
          <CardsSkeleton count={MONITOR_LOAD_MORE_SKELETON_CARDS} />
        )}
        {canLoadMore && <LoadMoreTrigger onLoadMore={loadMore} />}
      </>
    );
  }

  return (
    <div className={styles.monitor}>
      <MonitorFilters
        cityId={cityId}
        code={code}
        deliveryType={deliveryType}
        citiesOptions={citiesOptions}
        loadingCities={loadingCities}
        onChangeCity={setCityId}
        onChangeCode={setCode}
        onChangeDeliveryType={setDeliveryType}
      />

      <MonitorIndicators
        data={indicators}
        deliveryType={deliveryType}
        isLoading={loadingCities || isLoadingIndicators}
      />

      <MonitorStatusTabs
        activeTab={tab}
        counts={data?.counts}
        onChange={setTab}
        actions={
          lastUpdatedAt > 0 ? (
            <RefreshCountdown
              lastUpdatedAt={lastUpdatedAt}
              isRefreshing={isFetching}
            />
          ) : null
        }
      />

      {renderDeliveries()}

      <BackToTopButton />

      {drawerDeliveryId && (
        <DeliveryDetailDrawer
          deliveryId={drawerDeliveryId}
          open={!!deliveryId}
          onClose={closeDetail}
          onExited={() => setDrawerDeliveryId(undefined)}
        />
      )}
    </div>
  );
}

export { MonitorDeliveriesMain };
